import { MrfiktivSharedContentViewmodelGen } from "@/services/mrfiktiv/v1/data-contracts";
import Vue from "vue";
import { RefDocumentType } from "./RefDocument";

/**
 * Return type for the shared content
 */
export interface SharedContentDocument {
  refId: string;
  refType: string;
  refDocument: RefDocumentType;
  error?: Error;
}

/**
 * Type for strategy
 */
export interface FetchStrategy {
  /**
   * Checks if the strategy is for a certain refType
   * @param refType
   */
  canHandle(refType: string): boolean;

  /**
   * Fetch interface for strategy
   * @param partnerId
   * @param refId
   * @param sct
   */
  fetch(partnerId: string, refId: string, sct?: string): Promise<SharedContentDocument>;
}

/**
 * Registry to manage all strategies
 */
export class FetchStrategyRegistry {
  private strategies: FetchStrategy[] = [];

  /**
   * Add a strategy to the registry
   * @param strategy
   */
  register(strategy: FetchStrategy): void {
    this.strategies.push(strategy);
  }

  /**
   * Finds a strategy for a refType
   * @param refType
   * @returns
   */
  getStrategy(refType: string): FetchStrategy | undefined {
    return this.strategies.find(strategy => strategy.canHandle(refType));
  }
}

/**
 * Service to handle the fetching
 */
export class SharedContentFetchService {
  private registry: FetchStrategyRegistry;

  constructor(registry: FetchStrategyRegistry) {
    this.registry = registry;
  }

  /**
   * Fetches the content based on found strategy.
   * If strategy is missing return null refDocument with error.
   * @param partnerId
   * @param sharedContent
   * @param accessToken
   * @returns
   */
  async fetchContent(
    partnerId: string,
    sharedContent: MrfiktivSharedContentViewmodelGen,
    accessToken: string
  ): Promise<SharedContentDocument[]> {
    const fetchPromises: Array<Promise<SharedContentDocument>> = [];

    for (const ref of sharedContent.refs) {
      const strategy = this.registry.getStrategy(ref.refType);

      if (strategy) {
        fetchPromises.push(strategy.fetch(partnerId, ref.refId, accessToken));
      } else {
        fetchPromises.push(
          Promise.resolve({
            refId: ref.refId,
            refType: ref.refType,
            refDocument: null,
            error: new Error(`No strategy found for refType: ${ref.refType}`)
          })
        );
      }
    }

    const results = await Promise.all(fetchPromises);
    return results;
  }
}

/**
 * Helper function to safely execute fetch operations
 * @param refId
 * @param refType
 * @param fetchFn
 * @returns
 */
export async function safeFetch<T>(
  refId: string,
  refType: string,
  fetchFn: () => Promise<RefDocumentType>
): Promise<SharedContentDocument> {
  try {
    const refDocument = await fetchFn();

    return { refId, refType, refDocument };
  } catch (e) {
    Vue.$log.error(`Error fetching ${refType}:`, e);

    return {
      refId,
      refType,
      refDocument: null,
      error: e instanceof Error ? e : new Error(String(e))
    };
  }
}
